import { useEffect, useState, type User } from "react";
import { supabase } from "@/lib/supabase";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { splitSiteName } from "@/lib/siteName";
import AuthForm from "./components/AuthForm";
import Overview from "./components/Overview";
import ProductsManager from "./components/ProductsManager";
import EnquiriesManager from "./components/EnquiriesManager";
import AdminsManager from "./components/AdminsManager";
import SettingsManager from "./components/SettingsManager";
import TestimonialsManager from "./components/TestimonialsManager";
import TrashManager from "./components/TrashManager";

type Tab = "overview" | "products" | "enquiries" | "testimonials" | "admins" | "settings" | "trash";

interface AdminRow {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
}

const NAV_ITEMS: { key: Tab; label: string; icon: string }[] = [
  { key: "overview", label: "Dashboard", icon: "ri-dashboard-line" },
  { key: "products", label: "Products", icon: "ri-stack-line" },
  { key: "enquiries", label: "Enquiries", icon: "ri-inbox-line" },
  { key: "testimonials", label: "Testimonials", icon: "ri-chat-quote-line" },
  { key: "admins", label: "Admins", icon: "ri-shield-user-line" },
  { key: "settings", label: "Settings", icon: "ri-settings-3-line" },
  { key: "trash", label: "Trash", icon: "ri-delete-bin-6-line" },
];

export default function AdminPage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [admin, setAdmin] = useState<AdminRow | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState("");
  const [recovering, setRecovering] = useState(false);
  const [newEnquiryCount, setNewEnquiryCount] = useState(0);
  const { site } = useSiteSettings();
  const brand = splitSiteName(site.site_name);

  // Auth state listener — must return synchronously (no async/await inside)
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === "PASSWORD_RECOVERY") {
        setRecovering(true);
      } else if (event === "SIGNED_IN" || event === "USER_UPDATED") {
        setRecovering(false);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // Resolve admin status whenever the user changes
  useEffect(() => {
    if (!user) {
      setAdmin(null);
      return;
    }
    let active = true;

    const check = async () => {
      const byId = await supabase
        .from("admins")
        .select("id, email, full_name, role")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!active) return;
      if (byId.data) {
        setAdmin(byId.data as AdminRow);
        return;
      }

      const byEmail = await supabase
        .from("admins")
        .select("id, email, full_name, role")
        .eq("email", user.email ?? "")
        .maybeSingle();

      if (active) setAdmin((byEmail.data as AdminRow) || null);
    };

    check();
    return () => {
      active = false;
    };
  }, [user]);

  // Poll for new enquiries so the badge stays fresh while the admin is logged in.
  useEffect(() => {
    if (!admin) return;
    let active = true;
    const fetchCount = async () => {
      const { count } = await supabase
        .from("enquiries")
        .select("id", { count: "exact", head: true })
        .eq("status", "new")
        .is("deleted_at", null);
      if (active) setNewEnquiryCount(count ?? 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [admin]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const handleBootstrap = async () => {
    setBootstrapping(true);
    setBootstrapError("");
    try {
      const { data, error } = await supabase.functions.invoke("manage-admin", {
        body: { action: "bootstrap", full_name: user?.user_metadata?.full_name || "" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.admin) setAdmin(data.admin as AdminRow);
    } catch (err) {
      setBootstrapError(err instanceof Error ? err.message : "Failed to claim owner access");
    } finally {
      setBootstrapping(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-dark">
        <i className="ri-loader-4-line animate-spin text-4xl text-brand-gold"></i>
      </div>
    );
  }

  if (!user || recovering) {
    return <AuthForm initialMode={recovering ? "reset" : "signin"} />;
  }

  // Authenticated but not yet an admin
  if (!admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-dark px-4 py-10">
        <div className="w-full max-w-md rounded-lg bg-white p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-gold text-brand-dark">
            <i className="ri-shield-keyhole-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl font-semibold uppercase text-brand-dark">
            Access Restricted
          </h1>
          <p className="mt-2 text-sm text-neutral-500">
            You are signed in as <strong>{user.email}</strong>, but this account is not an admin yet.
          </p>

          {bootstrapError && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {bootstrapError}
            </div>
          )}

          <div className="mt-6 space-y-3">
            <button
              onClick={handleBootstrap}
              disabled={bootstrapping}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white disabled:opacity-60 whitespace-nowrap"
            >
              {bootstrapping && <i className="ri-loader-4-line animate-spin"></i>}
              {bootstrapping ? "Setting up…" : "Claim Owner Access"}
            </button>
            <button
              onClick={handleSignOut}
              className="w-full rounded-md border border-neutral-300 px-6 py-3 text-sm font-medium text-neutral-600 hover:bg-neutral-50 whitespace-nowrap"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col bg-brand-dark text-white md:flex">
        <div className="flex items-center gap-2 border-b border-white/10 px-5 py-5">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-gold text-brand-dark">
            <i className="ri-car-line text-2xl"></i>
          </span>
          <span className="leading-tight">
            <span className="font-heading text-base font-semibold uppercase block">{brand.first}</span>
            <span className="font-heading text-[10px] tracking-[0.25em] uppercase text-brand-gold block">
              {brand.second}
            </span>
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              className={`flex w-full items-center gap-3 rounded-md px-4 py-3 text-sm font-medium transition whitespace-nowrap ${
                tab === item.key
                  ? "bg-brand-gold text-brand-dark"
                  : "text-white/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              <i className={`${item.icon} text-lg`}></i>
              {item.label}
              {item.key === "enquiries" && newEnquiryCount > 0 && (
                <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
                  {newEnquiryCount}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gold text-brand-dark">
              <i className="ri-user-line"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{admin.full_name || "Admin"}</p>
              <p className="truncate text-xs text-white/50">{user.email}</p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-white/20 px-4 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10 whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line"></i>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-30 bg-brand-dark text-white md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-gold text-brand-dark">
              <i className="ri-car-line"></i>
            </span>
            <span className="font-heading text-sm font-semibold uppercase">{site.site_name} Admin</span>
          </div>
          <button onClick={handleSignOut} className="flex h-9 w-9 items-center justify-center rounded-md text-white/80">
            <i className="ri-logout-box-r-line text-xl"></i>
          </button>
        </div>
        <nav className="flex overflow-x-auto px-2 pb-2">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              onClick={() => setTab(item.key)}
              className={`flex shrink-0 items-center gap-2 rounded-md px-4 py-2 text-sm font-medium whitespace-nowrap ${
                tab === item.key ? "bg-brand-gold text-brand-dark" : "text-white/70"
              }`}
            >
              <i className={item.icon}></i>
              {item.label}
              {item.key === "enquiries" && newEnquiryCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
                  {newEnquiryCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      <main className="md:pl-60">
        <div className="mx-auto max-w-6xl p-4 md:p-8">
          {tab === "overview" && <Overview />}
          {tab === "products" && <ProductsManager />}
          {tab === "enquiries" && <EnquiriesManager />}
          {tab === "testimonials" && <TestimonialsManager />}
          {tab === "admins" && <AdminsManager />}
          {tab === "settings" && <SettingsManager />}
          {tab === "trash" && <TrashManager />}
        </div>
      </main>
    </div>
  );
}